/**
 * My Accountant page composer.
 *
 * MyAccountantHeader = sticky breadcrumb + title + subtitle (always visible at
 * the top of the content area; scrolling body slides underneath).
 *
 * Body renders:
 *   - EmptyState when no accountant (invite -> /invite, browse -> /find)
 *   - AccountantsTable when there is one (row-based list)
 *
 * Owns ConfirmModal (cancel / remove / dismiss / forget), inline toast, and
 * AccessDrawer for View access on active accountants.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMyAccountant } from "../../../hooks/useMyAccountant";
import { EmptyState } from "./components/EmptyState";
import { AccountantsTable } from "./components/AccountantsTable";
import { AccessDrawer } from "./components/AccessDrawer";
import { ConfirmModal } from "../Recurring/components/ConfirmModal";
import type { MyAccountant, PreviousAccountant } from "./myAccountantTypes";
import { PALETTE } from "./constants";

export function MyAccountantHeader() {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        background: "#ffffff",
        zIndex: 10,
        padding: "18px 48px 18px",
      }}
    >
      <p
        style={{
          fontSize: 12.5,
          color: PALETTE.ink3,
          margin: "0 0 4px",
          fontWeight: 500,
        }}
      >
        <span style={{ color: PALETTE.ink3 }}>Accounting</span>
        <span style={{ color: PALETTE.ink3, margin: "0 6px" }}>/</span>
        <span style={{ color: PALETTE.ink, fontWeight: 600 }}>
          My accountant
        </span>
      </p>
      <h1
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: PALETTE.ink,
          margin: "0 0 8px",
          letterSpacing: "-0.02em",
        }}
      >
        My accountant
      </h1>
      <p
        style={{
          fontSize: 14,
          color: PALETTE.ink2,
          margin: 0,
          lineHeight: 1.55,
          maxWidth: 680,
        }}
      >
        Invite your accountant to work with you inside Novala. They log in to
        see your transactions, categorize, reconcile, and prepare your filings.
        No more emailing CSVs.
      </p>
    </div>
  );
}

interface ConfirmState {
  title: string;
  description: string;
  confirmLabel: string;
  icon: "delete" | "end";
  onConfirm: () => void;
}

export default function MyAccountantPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data,
    previousAccountants,
    loading,
    resend,
    cancel,
    remove,
    dismissExpired,
    forgetPrevious,
  } = useMyAccountant();

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);

  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (state?.toast) {
      setToast(state.toast);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const goInvite = useCallback(() => {
    navigate("/accounting/my-accountant/invite");
  }, [navigate]);

  const goBrowse = useCallback(() => {
    navigate("/accounting/my-accountant/find");
  }, [navigate]);

  const goReinvite = useCallback(
    (prev: PreviousAccountant) => {
      const params = new URLSearchParams();
      if (prev.email) params.set("email", prev.email);
      if (prev.name) params.set("name", prev.name);
      if (prev.firmName) params.set("firm", prev.firmName);
      params.set("fromPrevious", prev.id);
      navigate(`/accounting/my-accountant/invite?${params.toString()}`);
    },
    [navigate],
  );

  const handleResend = useCallback(
    (id: string) => {
      resend(id);
      setToast("Invite resent");
    },
    [resend],
  );

  const handleViewAccess = useCallback((_a: MyAccountant) => {
    setAccessOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      if (!data || data.id !== id) return;
      const displayName = data.name?.trim() || data.email;

      if (data.status === "pending") {
        setConfirm({
          title: "Cancel this invite?",
          description: `This will cancel the invite sent to ${data.email}. They won't be able to accept it.`,
          confirmLabel: "Cancel invite",
          icon: "delete",
          onConfirm: () => {
            cancel(id);
            setConfirm(null);
            setToast("Invite cancelled");
          },
        });
      } else if (data.status === "active") {
        setConfirm({
          title: "Remove this accountant?",
          description: `${displayName} will lose access to your books immediately. You can invite them again later.`,
          confirmLabel: "Remove accountant",
          icon: "delete",
          onConfirm: () => {
            remove(id);
            setConfirm(null);
            setToast("Accountant removed");
          },
        });
      } else if (data.status === "expired") {
        setConfirm({
          title: "Dismiss this expired invite?",
          description: `The invite to ${data.email} has expired. Dismissing it clears it from your dashboard.`,
          confirmLabel: "Dismiss",
          icon: "end",
          onConfirm: () => {
            dismissExpired(id);
            setConfirm(null);
            setToast("Expired invite dismissed");
          },
        });
      }
    },
    [data, cancel, remove, dismissExpired],
  );

  const handleForget = useCallback(
    (prevId: string) => {
      const prev = previousAccountants.find((p) => p.id === prevId);
      if (!prev) return;
      const displayName = prev.name?.trim() || prev.email;
      setConfirm({
        title: "Remove from history?",
        description: `${displayName} will no longer appear in your previously connected list.`,
        confirmLabel: "Remove",
        icon: "delete",
        onConfirm: () => {
          forgetPrevious(prevId);
          setConfirm(null);
          setToast("Removed from history");
        },
      });
    },
    [previousAccountants, forgetPrevious],
  );

  if (loading) {
    return (
      <div
        style={{
          padding: "40px",
          color: PALETTE.ink2,
          fontSize: 13,
          background: "#ffffff",
          minHeight: "calc(100vh - 56px)",
        }}
      >
        Loading your accountant...
      </div>
    );
  }

  const accountants: MyAccountant[] = data ? [data] : [];
  const hasAccountant = accountants.length > 0;

  return (
    <>
      <div
        style={{
          width: "100%",
          background: "#ffffff",
          minHeight: "calc(100vh - 56px)",
          boxSizing: "border-box",
        }}
      >
        {hasAccountant ? (
          <AccountantsTable
            accountants={accountants}
            onResend={handleResend}
            onDelete={handleDelete}
            onViewAccess={handleViewAccess}
          />
        ) : (
          <EmptyState
            onInviteClick={goInvite}
            onBrowseClick={goBrowse}
            previousAccountants={previousAccountants}
            onReinvite={goReinvite}
            onForget={handleForget}
          />
        )}
      </div>

      {accessOpen && data && (
        <AccessDrawer
          isOpen={accessOpen}
          onClose={() => setAccessOpen(false)}
          accountant={data}
        />
      )}

      {confirm && (
        <ConfirmModal
          isOpen={true}
          variant="danger"
          icon={confirm.icon}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: PALETTE.ink,
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: "0 10px 24px -6px rgba(14, 26, 26, 0.3)",
            zIndex: 100,
            animation: "ma-toast-pop 220ms ease-out",
          }}
        >
          <style>{`
            @keyframes ma-toast-pop {
              from { opacity: 0; transform: translate(-50%, 8px); }
              to { opacity: 1; transform: translate(-50%, 0); }
            }
          `}</style>
          {toast}
        </div>
      )}
    </>
  );
}